import React from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrlWithApi } from '../config/api';
import PillToggleGroup from './ui/PillToggleGroup';

/** Nom de l'évènement émis quand la bibliothèque d'exercices a été retraduite. */
export const CATALOG_LOCALIZED_EVENT = 'kaiylo:exercise-catalog-localized';

/**
 * LanguageToggle — segmented FR | EN control mounted in the Header
 * settings dropdown. Owns:
 *   1) i18next.changeLanguage (synchronous in our config)
 *   2) localStorage 'kaiyloLanguage' write
 *   3) Fire-and-forget supabase.auth.updateUser({ data: { language } })
 *   4) Fire-and-forget PATCH /api/notifications/push/preferences { locale }
 *      so system notifications follow the language (push client loaded on
 *      demand — it must stay out of the main chunk)
 *
 * Does NOT mutate document.documentElement.lang directly — the
 * `languageChanged` listener installed in src/i18n/index.js owns that.
 * No toast (D8) and no UI loading state (D9 — optimistic write).
 */
const LANGUAGES = ['fr', 'en'];

const baseButtonStyle = {
  flex: 1,
  padding: '6px 10px',
  fontSize: '13px',
  fontWeight: 500,
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  transition: 'background-color 0.2s, color 0.2s',
  background: 'transparent',
  color: 'rgba(255, 255, 255, 0.55)',
};

const activeButtonStyle = {
  ...baseButtonStyle,
  background: 'rgba(212, 132, 90, 0.18)',
  color: '#d4845a',
};

// variant 'pill' : pastilles cuivre du volet Réglages coach (SettingsSidebar).
const LanguageToggle = ({ variant }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const current = i18n.language?.startsWith('en') ? 'en' : 'fr';

  // Coach : les exercices importés du catalogue Kaiylo et non renommés suivent
  // la langue. Silencieux comme l'écriture Supabase ; la page Exercices écoute
  // l'évènement pour se rafraîchir si elle est ouverte.
  const localizeExerciseCatalog = async (language) => {
    if (user?.role !== 'coach') return;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${getApiBaseUrlWithApi()}/exercises/localize-catalog`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ language }),
      });
      if (res.ok) window.dispatchEvent(new CustomEvent(CATALOG_LOCALIZED_EVENT, { detail: { language } }));
    } catch (err) {
      console.warn('[i18n] exercise catalog localization failed', err);
    }
  };

  const handleSelect = async (next) => {
    if (next === current) return; // no-op on active button
    await i18n.changeLanguage(next);
    try {
      localStorage.setItem('kaiyloLanguage', next);
    } catch (err) {
      // localStorage may be unavailable (e.g., private mode quota) —
      // not fatal, language still flipped in-session.
      console.warn('[i18n] localStorage write failed', err);
    }
    // Fire-and-forget Supabase write. Failures must NOT throw or
    // surface a toast (D8/D9). Log via console.warn for grepability.
    Promise.resolve(supabase.auth.updateUser({ data: { language: next } })).catch((err) => {
      console.warn('[i18n] language write-back failed', err);
    });
    localizeExerciseCatalog(next);
    // Same contract for the push language (account-level, read by the backend
    // for every system notification). No request when logged out.
    import('../lib/push/pushClient')
      .then((client) => client.syncPushLocale(next))
      .catch((err) => {
        console.warn('[push] locale write-back failed', err);
      });
  };

  if (variant === 'pill') {
    return (
      <PillToggleGroup
        ariaLabel={t('settings.language_label')}
        options={LANGUAGES.map((lng) => ({ value: lng, label: t(lng === 'fr' ? 'language.fr_label' : 'language.en_label') }))}
        value={current}
        onSelect={handleSelect}
        testIdPrefix="language"
      />
    );
  }

  return (
    <div
      role="group"
      aria-label={t('settings.language_label')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        width: '100%',
        padding: '4px',
        marginBottom: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        borderRadius: '8px',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {LANGUAGES.map((lng) => {
        const isActive = lng === current;
        return (
          <button
            key={lng}
            type="button"
            aria-pressed={isActive}
            onClick={() => handleSelect(lng)}
            style={isActive ? activeButtonStyle : baseButtonStyle}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {t(lng === 'fr' ? 'language.fr_label' : 'language.en_label')}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageToggle;
